import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface SubscriptionStore {
  isPremium: boolean;
  loading: boolean;
  error: string | null;
  checkPremiumStatus: () => Promise<void>;
  upgrade: () => Promise<void>;
}

export const useSubscriptionStore = create<SubscriptionStore>((set) => ({
  isPremium: false,
  loading: false,
  error: null,

  checkPremiumStatus: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: subscription, error } = await supabase
        .from('subscriptions')
        .select('is_premium')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      set({ isPremium: subscription?.is_premium || false });
    } catch (err) {
      console.error('Error checking premium status:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Failed to check premium status',
        isPremium: false
      });
    } finally {
      set({ loading: false });
    }
  },

  upgrade: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // First create or update RLS policy for subscriptions
      const { error: policyError } = await supabase.rpc('create_subscription_policies');
      if (policyError) throw policyError;

      // Check if subscription exists
      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingSub) {
        // Update existing subscription
        const { error } = await supabase
          .from('subscriptions')
          .update({ is_premium: true })
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Create new subscription
        const { error } = await supabase
          .from('subscriptions')
          .insert([{
            user_id: user.id,
            is_premium: true
          }]);

        if (error) throw error;
      }

      set({ isPremium: true });
    } catch (err) {
      console.error('Error upgrading to premium:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to upgrade to premium' });
      throw err;
    } finally {
      set({ loading: false });
    }
  }
}));